<template>
  <b-card
    class="chat-widget"
    no-body
  >
    <b-card-header>
      <div class="d-flex align-items-center">
        <b-avatar
          size="34"
          variant="light-primary"
          class="mr-50 badge-minimal"
          badge
          badge-variant="success"
        >
          {{ me.name.charAt(0) }}{{ me.surname.charAt(0) }}
        </b-avatar>
        <h5 class="mb-0">
          {{ me.name }} {{ me.surname }}
        </h5>
      </div>
      <!--      <feather-icon-->
      <!--        icon="MoreVerticalIcon"-->
      <!--        size="18"-->
      <!--      />-->
    </b-card-header>

    <section
      ref="chatWindow"
      class="chat-app-window d-flex flex-column"
    >
      <!-- User Chat Area -->
      <vue-perfect-scrollbar
        ref="refChatLogPS"
        :settings="perfectScrollbarSettings"
        :style="chatScrollAreaStyles"
        class="user-chats scroll-area flex-grow-1"
      >
        <comment :comment-data="comments" />
      </vue-perfect-scrollbar>

      <!-- Message Input -->
      <b-form
        ref="chatForm"
        class="chat-app-form mt-auto"
        @submit.prevent="addComment"
      >
        <b-input-group class="input-group-merge form-send-message mr-1">
          <b-form-input
            v-model="newComment"
            :placeholder="$t('order.label.enter-your-comment')"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
          />
        </b-input-group>
        <b-button
          variant="primary"
          type="submit"
          :disabled="!newComment"
        >
          <feather-icon icon="SendIcon" />
        </b-button>
      </b-form>
    </section>
  </b-card>
</template>
<script>
import {
  BCard, BCardHeader, BAvatar, BForm, BFormInput, BInputGroup, BButton,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Comment from '@/views/orders/partials/comments/Comment.vue'
import { transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import moment from 'moment'

export default {
  name: 'OrderComments',
  components: {
    BCard,
    BCardHeader,
    BAvatar,
    BForm,
    BFormInput,
    BInputGroup,
    BButton,

    // SFC
    Comment,

    // 3rd party
    VuePerfectScrollbar,
  },
  props: {
    order: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      newComment: '',
      perfectScrollbarSettings: {
        maxScrollbarLength: 150,
        wheelPropagation: false,
      },
      chatScrollAreaHeight: null,
      resizeObserver: null,
      windowResizeHandler: null,
    }
  },
  computed: {
    me() {
      return this.$store.getters['auth/getUser']
    },
    comments() {
      return this.order.comments
    },
    chatScrollAreaStyles() {
      if (this.chatScrollAreaHeight === null) {
        return {}
      }
      return {
        height: `${this.chatScrollAreaHeight}px`,
        maxHeight: `${this.chatScrollAreaHeight}px`,
      }
    },
  },
  mounted() {
    this.psToBottom()
    this.initScrollAreaSizing()
  },
  beforeDestroy() {
    this.destroyScrollAreaSizing()
  },
  methods: {
    formatDate(date) {
      return transformDatetimeToHumanReadablePretty(date)
    },
    async addComment() {
      const trimmedComment = (this.newComment || '').trim()
      if (!trimmedComment) return

      const previousState = {
        comments: [...this.order.comments],
        newComment: this.newComment,
      }

      const comment = {
        author: {
          id: this.me.id,
          name: this.me.name,
          surname: this.me.surname,
        },
        comment: trimmedComment,
        createdAt: moment(),
      }

      this.newComment = ''
      this.order.comments.push(comment)
      this.$nextTick(() => {
        this.psToBottom()
      })

      try {
        await this.$store.dispatch('order/addComment', { id: this.order.id, comment })
        this.$nextTick(() => {
          this.psToBottom()
        })
      } catch (error) {
        this.newComment = previousState.newComment
        this.$set(this.order, 'comments', previousState.comments)

        if (window?.toast?.notify?.danger) {
          window.toast.notify.danger('Błąd', 'Nie udało się dodać komentarza. Spróbuj ponownie.')
        }
      }
    },
    psToBottom() {
      const scrollEl = this.$refs.refChatLogPS.$el || this.$refs.refChatLogPS
      scrollEl.scrollTop = scrollEl.scrollHeight
    },
    initScrollAreaSizing() {
      // Keep the scrollable log capped to the card height so extra comments don't stretch the column.
      this.$nextTick(() => {
        this.updateScrollAreaHeight()
        this.bindResizeObserver()
      })
    },
    bindResizeObserver() {
      const chatWindowEl = this.$refs.chatWindow
      const hasWindow = typeof window !== 'undefined'

      if (!chatWindowEl || !hasWindow) return

      if (typeof window.ResizeObserver === 'function') {
        this.resizeObserver = new window.ResizeObserver(() => {
          this.updateScrollAreaHeight()
        })
        this.resizeObserver.observe(chatWindowEl)
      } else {
        this.windowResizeHandler = () => {
          this.updateScrollAreaHeight()
        }
        window.addEventListener('resize', this.windowResizeHandler, { passive: true })
      }
    },
    destroyScrollAreaSizing() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      if (this.windowResizeHandler) {
        window.removeEventListener('resize', this.windowResizeHandler)
        this.windowResizeHandler = null
      }
    },
    updateScrollAreaHeight() {
      this.$nextTick(() => {
        const chatWindowEl = this.$refs.chatWindow
        const chatFormEl = this.$refs.chatForm?.$el || this.$refs.chatForm
        if (!chatWindowEl || !chatFormEl) return

        const availableHeight = chatWindowEl.clientHeight - chatFormEl.clientHeight
        this.chatScrollAreaHeight = Math.max(availableHeight, 0)
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/base/pages/app-chat-list.scss";

.chat-widget {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: calc(#{$chat-widget-height} + #{$chat-widget-head-footer-height});

  .chat-app-window {
    min-height: calc(#{$chat-widget-height} + #{$chat-widget-head-footer-height});
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
  }

  .chat-app-window .user-chats {
    flex: 1 1 auto;
    min-height: 0;
  }
}
</style>
